import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highcharts3d from 'highcharts/highcharts-3d';
import { useDispatch } from 'react-redux';
import { getBatteryChargeData } from '../../redux/action/action';
import { format, addMinutes } from 'date-fns';
import { UseContextData } from '../../context/dateContext';
import { useTranslation } from 'react-i18next';

highcharts3d(Highcharts);

const BatterynetChargeChart = ({ togglePopup, isDarkTheme }) => {
  const [chartData, setChartData] = useState();
  const dispatch = useDispatch();
  const [dynamicLabels, setDynamicLabels] = useState([]);
  const { formatDate } = UseContextData();
  const IndexValue = useRef(0);
  const TextColor = isDarkTheme ? "#ffffff" : "#000000";
  const backgroundColor = isDarkTheme ? "#222222" : "#ffffff";
  const { t } = useTranslation();

  useEffect(() => {
    const loadData = async () => {
      const requestParams = { index: IndexValue.current };
      try {
        const response = await dispatch(getBatteryChargeData(requestParams));
        const responseData = response?.payload?.data;
        if (responseData) {
          const modifiedData = {
            battery_charge_start: responseData?.battery_charge_start?.map(
              (value) => value * 100
            ),
            battery_charge_end: responseData?.battery_charge_end?.map(
              (value) => value * 100
            ),
          };
          setChartData(modifiedData);
          setDynamicLabels(generateTimeLabels(7));
          IndexValue.current = response?.payload?.last_index || 0;
          if (
            modifiedData.battery_charge_start.some((value) => value < 40) ||
            modifiedData.battery_charge_end.some((value) => value < 40)
          ) {
            togglePopup(true);
          } else {
            togglePopup(false);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    loadData();

    const intervalId = setInterval(() => {
      loadData();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [dispatch, formatDate]);

  const generateTimeLabels = (length) => {
    const previousTimes = Array.from({ length }, (_, index) => format(addMinutes(formatDate, -index), 'hh:mm'));
    return previousTimes.reverse();
  };

  const options = {
    chart: {
      type: 'area',
      height: '310px',
      options3d: {
        enabled: true,
        alpha: 15,
        beta: 332,
        depth: 100,
      },
      backgroundColor: backgroundColor,
    },
    title: {
      style: {
        display: 'none'
      }
    },

    xAxis: {
      categories: dynamicLabels || [],
      labels: {
        rotation: -45,
        step: 1,
        style: {
          color: TextColor,
        },
      },
    },
    yAxis: {
      title: {
        text: 'SoC(%)',
        offset: 50,
        style: {
          color: TextColor,
        },
      },
      labels: {
        style: {
          color: TextColor,
        },
      },
    },
    zAxis: {
      title: {
        text: '',
      },
    },
    accessibility: {
      enabled: false,
    },
    legend: {
      itemStyle: {
        color: TextColor,
      },
    },
    plotOptions: {
      area: {
        fillOpacity: 0.8,
        depth: 70,
        states: {
          inactive: {
            enabled: false
          }
        }
      },
      series: {
        depth: 45,
        animation: true,
        marker: {
          symbol: 'circle',
          radius: 5,
          lineWidth: 2,
          lineColor: '#fff',
        },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: t('Battery Charge Start'),
        color: 'rgba(183, 160, 88, 0.6)',
        data: (chartData?.battery_charge_start || []).map((value, index) => ({
          y: parseFloat(value.toFixed(2)),
          marker: {
            enabled: value < 40,
            fillColor: '#ff0000',
            symbol: 'circle',
            radius: 5,
            lineWidth: 2,
            lineColor: '#fff',
          },
        })),
      },
      {
        name: t('Battery Charge End'),
        color: 'rgba(122, 154, 188, 0.6)',
        data: (chartData?.battery_charge_end || []).map((value, index) => ({
          y: parseFloat(value.toFixed(2)),
          marker: {
            enabled: value < 40,
            fillColor: '#ff0000',
            symbol: 'circle',
            radius: 5,
            lineWidth: 2,
            lineColor: '#fff',
          },
        })),
      },
    ],
  };
  return (
      <div>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
  )
}

export default BatterynetChargeChart